import { HANDSHAKE_REQUIRED_STATUS } from '@breetometer/utils/secure-proxy/protocol';
import {
	type BaseQueryApi,
	createApi,
	type FetchArgs,
	fetchBaseQuery,
	type FetchBaseQueryError,
	type FetchBaseQueryMeta,
	type QueryReturnValue,
} from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';
import { Mutex } from 'async-mutex';
import {
	ENV_TARGET_KEY,
	getApiError,
	HIDE_TOAST_KEY,
	type IApiResponse,
	isSandboxEnvironment,
	LIVE_ENVIRONMENT,
	SANDBOX_ENVIRONMENT,
	SHOW_TOAST_KEY,
	showToast,
	type TargetEnvTypes,
} from 'BreetHelpers';

import {
	createClientHandshakeTelemetry,
	extractRetryEvidence,
	extractSecureProxyStatusCode,
	preserveRetryEnvironment,
	SECURE_PROXY_CHALLENGE_HEADER_NAME,
	SECURE_PROXY_TICKET_HEADER_NAME,
	shouldRecoverHandshake,
	TrustedAppIds,
} from './handshakeRecovery';

/**
 * Browser-side handshake telemetry — mirrors the server's Sentry reporting so the
 * "client" dashboard rows line up with the server rows. Fire-and-forget; records only
 * the request pathname, status, SPX code, and x-breet-trace-id (never headers/cookies/
 * bodies), and never affects the request flow.
 */
const handshakeTelemetry = createClientHandshakeTelemetry({
	appId: TrustedAppIds.breetPartners,
	reporter: {
		addBreadcrumb: (breadcrumb) => {
			Sentry.addBreadcrumb(breadcrumb);
		},
		captureMessage: (message, context) => {
			Sentry.captureMessage(message, context);
		},
	},
});

/** The request path (no query) for telemetry — `args` is a URL string or a FetchArgs. */
const telemetryPath = (args: string | FetchArgs): string => (typeof args === 'string' ? args : args.url);

/**
 * HTTP layer — client side of the secure-proxy handshake.
 * (Server side: vite-secure-proxy/*; full architecture: docs/SECURE_PROXY_ARCHITECTURE.md.)
 *
 * All requests go to the same-origin Vite proxy middleware, never to the backend
 * directly:
 *
 *   baseQuery       → /proxy/<path>       plain relay
 *   secureBaseQuery → /proxy/sec/<path>   server-signed relay (trusted-source handshake)
 *
 * Endpoints ONLY ever run through `baseQueryWithReauth`, which promotes/heals
 * automatically:
 *
 *   1. 419 PROMOTION: when the backend answers 419 (endpoint requires a
 *      trusted-source signature), the base proxy attaches one-time retry evidence —
 *      the x-fbc-bx ticket + x-fbc-ct challenge headers (plus HttpOnly binding
 *      cookies the browser stores by itself). `runHandshakeFlow` replays the
 *      identical request via `secureBaseQuery` with both headers echoed, at most
 *      once per request.
 *
 *   2. HANDSHAKE RECOVERY: the one-time nonces live in the memory of the replica
 *      that minted them (Caddy pins the browser to it via a sticky lb cookie). If
 *      that replica was replaced mid-handshake — rolling deploy, dev-server
 *      restart — /proxy/sec rejects with 403 + SPX_*_STATE_CONSUME_FAILED. That
 *      failure is transient, not hostile, so the wrapper re-runs the WHOLE
 *      handshake once (fresh /proxy call → fresh 419 → fresh evidence → fresh
 *      /proxy/sec); every other 403 surfaces to the caller untouched. See
 *      handshakeRecovery.ts for the predicate and the full rationale.
 *
 *   3. AUTH REFRESH: 401s trigger a mutex-serialised token refresh; if that fails
 *      the user is logged out and redirected.
 */

const prepareHeaders = (headers: Headers, { getState }: Pick<BaseQueryApi, 'getState'>) => {
	const pinnedEnv = headers.get(ENV_TARGET_KEY);
	if (pinnedEnv) {
		headers.set('x-breet-env', pinnedEnv);
		headers.delete(ENV_TARGET_KEY);
		return headers;
	}

	const app = (
		getState() as {
			app?: { targetEnvironment?: TargetEnvTypes; linkActionTarget?: string; isUserLoggedIn?: boolean };
		}
	).app;

	// Logged-out email-link flows (invite / reset-password / verify-email): a target carried on the link
	// drives x-breet-env for every request in the flow, until it's cleared when the action completes.
	// Never applied once a session is active.
	if (!app?.isUserLoggedIn && app?.linkActionTarget) {
		headers.set('x-breet-env', app.linkActionTarget);
		return headers;
	}

	headers.set('x-breet-env', isSandboxEnvironment(app?.targetEnvironment) ? SANDBOX_ENVIRONMENT : LIVE_ENVIRONMENT);
	return headers;
};

// Ceiling on every request so a stalled endpoint aborts client-side instead of hanging
// indefinitely (RTK Query surfaces a TIMEOUT_ERROR the caller can react to). Kept generous
// enough for slow-but-legitimate calls (e.g. exports) while still bounding the wait.
const REQUEST_TIMEOUT_MS = 60_000;

const baseQuery = fetchBaseQuery({
	baseUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/proxy`,
	prepareHeaders,
	credentials: 'include',
	timeout: REQUEST_TIMEOUT_MS,
});

const secureBaseQuery = fetchBaseQuery({
	baseUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/proxy/sec`,
	prepareHeaders,
	credentials: 'include',
	timeout: REQUEST_TIMEOUT_MS,
});

const createRetryHeaders = (headers: FetchArgs['headers'] | undefined): Headers => {
	const retryHeaders = new Headers();
	if (!headers) {
		return retryHeaders;
	}

	if (headers instanceof Headers) {
		headers.forEach((value, key) => retryHeaders.set(key, value));
		return retryHeaders;
	}

	if (Array.isArray(headers)) {
		headers.forEach((entry) => {
			const [key, value] = entry;
			if (key) {
				retryHeaders.set(key, value);
			}
		});
		return retryHeaders;
	}

	Object.entries(headers).forEach(([key, value]) => {
		if (value !== undefined) {
			retryHeaders.set(key, value);
		}
	});

	return retryHeaders;
};

const buildSecureRetryArgs = (
	args: string | FetchArgs,
	result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
): FetchArgs | null => {
	const evidence = extractRetryEvidence(result.meta?.response?.headers);
	if (!evidence) {
		return null;
	}

	const retryHeaders = createRetryHeaders(typeof args === 'string' ? undefined : args.headers);
	retryHeaders.set(SECURE_PROXY_TICKET_HEADER_NAME, evidence.retryTicket);
	retryHeaders.set(SECURE_PROXY_CHALLENGE_HEADER_NAME, evidence.browserChallenge);
	preserveRetryEnvironment(retryHeaders, result.meta?.response?.headers);

	return typeof args === 'string' ? { headers: retryHeaders, url: args } : { ...args, headers: retryHeaders };
};

const handleToastNotification = (
	result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
	isGetRequest: boolean
) => {
	const successResp = result.data as IApiResponse | undefined;
	const errorResp = result.error?.data as IApiResponse | undefined;
	const isToastHidden = !!result.meta?.request.headers.get(HIDE_TOAST_KEY);
	// GETs are muted by default (background reads would spam the user), but an endpoint that performs an
	// action over GET can opt back in — otherwise its response message is silently dropped.
	const isToastForced = !!result.meta?.request.headers.get(SHOW_TOAST_KEY);

	if (!isToastHidden && (!isGetRequest || isToastForced)) {
		showToast(successResp?.message ? 'success' : 'error', successResp?.message ?? errorResp?.message ?? 'Something went wrong');
	}
};

const handleServerError = (result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>) => {
	const errorReqStatusCode = typeof result.error?.status === 'number' && result.error.status;

	if (errorReqStatusCode && errorReqStatusCode >= 500) {
		showToast('error', 'Internal server error. Please be patient 🙏');
		return true;
	}
	return false;
};

export const ACCOUNT_SUSPENDED_SIGNATURE = 'your account is suspended. please contact support for more details.';
export const ACCOUNT_SELF_SUSPENDED_SIGNATURE = 'your account has been suspended, please reset your password to lift it';

export const isAccountError = (error: FetchBaseQueryError | undefined, signature: string): boolean =>
	!!error &&
	error.status === 403 &&
	!extractSecureProxyStatusCode(error.data) &&
	(getApiError(error) || '').toLowerCase().includes(signature);

const forceLogoutError = async (
	api: BaseQueryApi,
	result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
	signature: string
) => {
	// A suspended account fails every in-flight request simultaneously; once the first has logged us out the
	// rest are no-ops. Keying on the live session (not a module flag) self-resets on the next login.
	if (!(api.getState() as { app?: { isUserLoggedIn?: boolean } }).app?.isUserLoggedIn) return;

	api.dispatch({ type: 'app/logUserOut', payload: undefined });
	showToast('error', getApiError(result.error) || signature);

	const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
	if (currentPath !== '/login' && currentPath !== '/getlost') {
		const { default: router } = await import('../../../router');
		router.navigate('/login', { replace: true }).catch(console.error);
	}
};

const mutex = new Mutex();

const REFRESH_TIMEOUT_MS = 10_000;

/**
 * Runs the full proxy flow for one request: the plain /proxy call plus — when the
 * backend answers 419 (endpoint requires a trusted-source signature) — a single
 * promotion to /proxy/sec with the one-time evidence headers echoed back (the
 * HttpOnly binding cookies travel by themselves). `retriedWithSigned` records
 * whether the promotion happened so the recovery predicate can tell a failed
 * signed retry apart from a plain 403.
 */
const runHandshakeFlow = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: object) => {
	let retriedWithSigned = false;
	let result = await baseQuery(args, api, extraOptions);

	// (1) 419 PROMOTION — see the module docblock. Retry through the secure proxy.
	if (result.error?.status === HANDSHAKE_REQUIRED_STATUS) {
		const secureRetryArgs = buildSecureRetryArgs(args, result);
		if (secureRetryArgs) {
			retriedWithSigned = true;
			handshakeTelemetry.onPromotion({ path: telemetryPath(args) });
			result = await secureBaseQuery(secureRetryArgs, api, extraOptions);
		}
	}

	return { result, retriedWithSigned };
};

const runHandshakeFlowWithRecovery = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: object) => {
	let { result, retriedWithSigned } = await runHandshakeFlow(args, api, extraOptions);
	let recovered = false;

	if (
		shouldRecoverHandshake({
			data: result.error?.data,
			handshakeRecovered: false,
			retriedWithSigned,
			status: result.error?.status,
		})
	) {
		handshakeTelemetry.onRecovery({ code: extractSecureProxyStatusCode(result.error?.data), path: telemetryPath(args) });
		recovered = true;
		({ result } = await runHandshakeFlow(args, api, extraOptions));
	}

	const guardSpxCode = extractSecureProxyStatusCode(result.error?.data);
	if (result.error?.status === 403 && guardSpxCode) {
		const failure = {
			code: guardSpxCode,
			path: telemetryPath(args),
			status: 403,
			traceId: result.meta?.response?.headers.get('x-breet-trace-id') ?? null,
		};
		if (recovered) {
			handshakeTelemetry.onRecoveryFailed(failure);
		} else {
			handshakeTelemetry.onGuardRejection(failure);
		}
	}

	return { recovered, result };
};

const baseQueryWithReauth = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: object) => {
	await mutex.waitForUnlock();

	let { result } = await runHandshakeFlowWithRecovery(args, api, extraOptions);

	if (isAccountError(result.error, ACCOUNT_SUSPENDED_SIGNATURE)) {
		await forceLogoutError(api, result, ACCOUNT_SUSPENDED_SIGNATURE);
		return result;
	}

	if (isAccountError(result.error, ACCOUNT_SELF_SUSPENDED_SIGNATURE)) {
		await forceLogoutError(api, result, ACCOUNT_SELF_SUSPENDED_SIGNATURE);
		return result;
	}

	if (handleServerError(result)) {
		return result;
	}

	result = await attemptTokenRefresh(api, extraOptions, args, result);

	if (result.error?.status !== 401) {
		handleToastNotification(result, result.meta?.request.method === 'GET');
	}

	if (!result.error && result.data && result.meta?.response?.status && !(result.data instanceof Blob)) {
		result = {
			data: {
				...(result.data as object),
				statusCode: result.meta.response.status,
			},
			meta: result.meta,
		};
	}

	return result;
};

const refreshAndRetry = async (api: BaseQueryApi, extraOptions: object, args: string | FetchArgs) => {
	if (mutex.isLocked()) {
		await mutex.waitForUnlock();
		return (await runHandshakeFlowWithRecovery(args, api, extraOptions)).result;
	}

	const release = await mutex.acquire();
	try {
		const refreshAttempt = await runHandshakeFlowWithRecovery(
			{ url: '/auth/refresh', timeout: REFRESH_TIMEOUT_MS },
			api,
			extraOptions
		);
		if (!refreshAttempt.result.data) throw new Error();

		return (await runHandshakeFlowWithRecovery(args, api, extraOptions)).result;
	} catch (_e: unknown) {
		api.dispatch({ payload: undefined, type: 'app/logUserOut' });
		showToast('error', 'Session timeout, please log in');

		if (typeof window !== 'undefined') {
			window.location.replace('/login');
		}

		throw _e;
	} finally {
		release();
	}
};

const attemptTokenRefresh = async (
	api: BaseQueryApi,
	extraOptions: object,
	args: string | FetchArgs,
	resultArg: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
) => {
	if (resultArg.error?.status !== 401) return resultArg;

	const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
	if (currentPath === '/login' || currentPath === '/getlost') return resultArg;

	const state = api.getState() as { app?: { isUserLoggedIn?: boolean } };
	if (state.app && !state.app.isUserLoggedIn) return resultArg;

	if (resultArg.meta?.response?.url.includes('kyb')) {
		api.dispatch({ payload: undefined, type: 'kybAuth/logKybUserOut' });
	}

	try {
		return await refreshAndRetry(api, extraOptions, args);
	} catch {
		return resultArg;
	}
};

export const apiSlice = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithReauth,
	endpoints: () => ({}),
	tagTypes: [
		'Kyb',
		'User',
		'GetBanks',
		'GetBeneficiaries',
		'GetUserWallets',
		'GetUserPreference',
		'GenerateOTP',
		'GetSellAssets',
		'GetNotifications',
		'AllTransactions',
		'UserRewardPoints',
		'GetSavedWalletAddresses',
		'GetDeveloperIntegration',
		'GetTradesSubAccountWalletAddresses',
		'GetWebhookLogs',
		'GetWebhookLogById',
		'AllTeamMembers',
		'GetKybDetails',
	],
});

/**
 * Response handler for CSV export endpoints. The backend answers an export request with either the
 * file (a `text/csv` body) or, when it can't stream inline — e.g. over ~1000 rows, or an empty
 * result — a JSON message such as `{ message: 'csv will be sent to email' }`. Blobbing the response
 * unconditionally saves that JSON as a corrupt `.csv` and swallows the message, so we branch on the
 * `Content-Type`: JSON bodies are parsed and returned (the export listener surfaces them as a toast),
 * everything else is treated as the downloadable file.
 */
export const exportResponseHandler = async (response: Response): Promise<Blob | { message?: string }> => {
	const contentType = response.headers.get('content-type') ?? '';
	if (contentType.includes('application/json')) return (await response.json()) as { message?: string };
	return response.blob();
};
